/* eslint-disable simple-import-sort/imports*/
import type { DetectedResourceAttributes } from '@opentelemetry/resources'
import type { MetaApp } from '../config/types'

export type MatchUrlDefinitions = Array<string | RegExp>

/**
 * The `export enum MetricsExporter` is defining an enumeration with two possible values: `CONSOLE` and
 * `OTLP`. This enumeration is used to specify the type of exporter to use for exporting metrics.
 */
export enum MetricsExporter {
  CONSOLE = 0,
  OTLP = 1,
}

/**
 * The `export enum TracesExporter` is defining an enumeration with two possible values: `CONSOLE` and`OTLP`. This enumeration is used to specify the type of exporter to use for exporting traces.
 */
export enum TracesExporter {
  CONSOLE = 0,
  OTLP = 1,
}

export interface TracingInstrumentationOptions {
  /**
   * `app` is an optional property that allows the user to specify information about the application being instrumented.
   */
  app?: MetaApp
  /**
   * `exporterType` is an optional property  that allows the user to specify the type of exporter to use for exporting traces.
   */
  exporterType?: TracesExporter
  /**
   * `headers` is an optional property containing custom headers to be sent with each request
   */
  headers?: Record<string, string>
  /**
   *  `concurrencyLimit` is an optional property allows the user to specify a limit on the number of pending requests.
   *  This can be useful in preventing overload on the server or network by limiting the number of requests that can be made at once.
   */
  concurrencyLimit?: number
  /**
   * `instrumentationOptions` is an optional property that allows the user to specify additional options for the instrumentation.
   */
  instrumentationOptions?: {
    propagateTraceHeaderCorsUrls: MatchUrlDefinitions
  }
  /**
   *  `resourceAttributes` is an optional property that allows the user to specify additional attributes to be included in the
   * resource created by the OpenTelemetry SDK. These attributes can be used to provide additional context about the
   * application being instrumented, such as its environment or deployment location.
   */
  resourceAttributes?: DetectedResourceAttributes
  /**
   *  The `onExceptionAction` property is an optional function that can be passed as an option to the
   * `TracingInstrumentationOptions`. It is a callback function that will be called when an exception
   * occurs during the execution of the instrumentation. The purpose of this function is
   * to allow the user to handle any exceptions that occur during the instrumentation, such as logging
   * the error or sending an alert to the development team.
   */
  onExceptionAction?: (error: Error | undefined) => void
}

export interface MetricsInstrumentationOptions {
  /**
   *  `app` is an optional property that allows the user to specify information about the application being instrumented.
   */
  app?: MetaApp
  /**
   * `exporterType` is an optional property  that allows the user to specify the type of exporter to use for exporting metrics.
   */
  exporterType?: MetricsExporter
  /**
   * `headers` is an optional property containing custom headers to be sent with each request
   * */
  headers?: Record<string, string>
  /**
   * `instrumentationOptions` is an optional property that allows the user to specify additional options for the instrumentation.
   */
  instrumentationOptions?: {
    propagateTraceHeaderCorsUrls: MatchUrlDefinitions
  }
}
